import { useCallback, useState } from 'react';
import useFetch, { ServiceParams } from '../hooks/useFetch';
import EndPoints from '../config/EndPoint';
import { Shift } from '../models/ShiftModel';

const qs = (filters?: Record<string, any>) => {
  if (!filters) return '';
  return Object.entries(filters)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
};

const useShiftsService = () => {
  const { doGet, doPost, doPut, doDelete } = useFetch();
  const [fetching, setFetching] = useState<Record<string, boolean>>({});

  const setFlag = useCallback((key: string, val: boolean) => {
    setFetching(prev => ({ ...prev, [key]: val }));
  }, []);

  const list = useCallback(
    (filters?: Record<string, any>, events?: any, signal?: AbortSignal) =>
      doGet({ 
        url: `${EndPoints.SHIFTS.LIST}${qs(filters) ? `?${qs(filters)}` : ""}`, 
        setFetching: v => setFlag('list', v), 
        signal, 
        ...events 
      } as ServiceParams),
    [doGet, setFlag]
  );

  const store = useCallback(
    (payload: Shift, events?: any) =>
      doPost({ url: EndPoints.SHIFTS.STORE, body: payload, setFetching: v => setFlag('store', v), ...events } as ServiceParams),
    [doPost, setFlag]
  );

  const update = useCallback(
    (id: number, payload: Shift, events?: any) =>
      doPut({ url: EndPoints.SHIFTS.UPDATE.replace(':id', String(id)), body: payload, setFetching: v => setFlag('update', v), ...events } as ServiceParams),
    [doPut, setFlag]
  );

  const remove = useCallback(
    (id: number, events?: any) =>
      doDelete({ url: EndPoints.SHIFTS.DELETE.replace(':id', String(id)), setFetching: v => setFlag('delete', v), ...events } as ServiceParams),
    [doDelete, setFlag]
  );

  return { fetching, list, store, update, remove };
};

export default useShiftsService;
